import { Component, KeyboardEvent, ReactNode } from 'react';

import styles from './tab-strip.component.module.scss';

export interface TabDefinition {
    /** Stable id; also the value reported to `onSelect` and the `aria-controls` target's suffix. */
    id: string;
    label: string;
    /** A short word rendered after the label ("NEW"). Decorative — it is not announced. */
    badge?: string;
    /**
     * An icon rendered before the label (the Studio's phone tabs, Mobile.png). Decorative: it
     * must carry `aria-hidden` itself (the Studio's `icons.*` do), so the tab's name stays the
     * label. Additive (R3-F1); every existing strip renders without it.
     */
    icon?: ReactNode;
    disabled?: boolean;
}

interface TabStripProps {
    tabs: TabDefinition[];
    selectedId: string;
    onSelect: (id: string) => void;
    /** Accessible name for the tablist itself, e.g. "Converter modes". */
    label: string;
    /**
     * `aria-controls` is `${panelIdPrefix}-${tab.id}`; the panel must carry that id and
     * `role="tabpanel"`. Optional because a strip that switches a whole page body has no single
     * panel element to point at, and a WRONG aria-controls is worse than none (it names an
     * element that is not there).
     */
    panelIdPrefix?: string;
    /**
     * Namespace for each tab's DOM id and `data-testid` (`${idPrefix}-${tab.id}`, default `tab`).
     * Two strips on one page that share a tab id would otherwise emit duplicate ids, and a panel
     * has nothing stable to point `aria-labelledby` at.
     */
    idPrefix?: string;
    /**
     * `pill` (default) is the inline strip. `bar` is the same widget laid out as a full-width
     * row -- what the Studio's mobile artboard draws along the bottom. Styling only: the roles,
     * the roving tabindex and the key handling are the same, which is the point of having one
     * tablist in the phase rather than two (review C1).
     */
    variant?: 'pill' | 'bar';
    className?: string;
    testId?: string;
}

/**
 * The accessible tab strip -- STUDIO_DESIGN.md 6.1, and the shared leaf the Frame picker and the
 * Studio agreed on rather than coupling to each other (STUDIO_PLAN_P2/progress.md:50,52).
 *
 * ROVING TABINDEX, not tabindex-on-every-tab: the WAI-ARIA tabs pattern puts ONE tab in the tab
 * order and moves between tabs with the arrow keys, so a keyboard user tabs past the strip in one
 * press instead of once per tab. Selection follows focus (arrow = select), which is the pattern's
 * "automatic activation" variant and the right one here because switching costs nothing -- the
 * Studio body is a dynamic import that is already resolved once it has been opened.
 *
 * It is a CLASS component (owner ruling, 2026-09-11) and it takes no children: a tab strip that
 * rendered its own panel would decide the page's layout, which is the caller's job.
 */
export class TabStripComponent extends Component<TabStripProps> {
    private readonly refs_: Map<string, HTMLButtonElement> = new Map();

    private setRef = (id: string) => (el: HTMLButtonElement | null) => {
        if (el) this.refs_.set(id, el);
        else this.refs_.delete(id);
    };

    private enabledTabs(): TabDefinition[] {
        return this.props.tabs.filter((t) => !t.disabled);
    }

    /**
     * The ONE tab in the page's tab order.
     *
     * Normally the selected one -- but a `selectedId` that matches no ENABLED tab (a document
     * carrying a transition type this strip does not offer, pre-flight row 40) would otherwise
     * leave every button at -1 and the whole widget unreachable by keyboard (review I2). The
     * first enabled tab takes the roving slot in that case; it is not marked selected.
     */
    private rovingId(): string | undefined {
        const { tabs, selectedId } = this.props;
        if (tabs.some((t) => t.id === selectedId && !t.disabled)) return selectedId;
        return this.enabledTabs()[0]?.id;
    }

    /**
     * Wrap: the pattern's Left on the first tab goes to the last one.
     *
     * An unmatched `selectedId` -- a disabled or unknown tab -- has no NEXT tab, so the first arrow
     * press lands ON the roving tab rather than stepping past it. Stepping from `findIndex === -1`
     * made ArrowLeft land on tab n-2 and ArrowRight on tab 0 while the roving stop was elsewhere,
     * i.e. focus jumped somewhere the user had never been. `SegmentedComponent.move()` was fixed
     * this way in P5b.5 (its review C2) and these two leaves answer the same keys; review M2 is
     * that they must answer them the same way.
     */
    private move(delta: number) {
        const tabs = this.enabledTabs();
        if (tabs.length === 0) return;
        const current = tabs.findIndex((t) => t.id === this.props.selectedId);
        if (current < 0) {
            const roving = this.rovingId();
            if (roving !== undefined) this.select(roving, true);
            return;
        }
        const next = tabs[(current + delta + tabs.length) % tabs.length];
        this.select(next.id, true);
    }

    private select(id: string, focus: boolean) {
        if (id !== this.props.selectedId) this.props.onSelect(id);
        if (focus) this.refs_.get(id)?.focus();
    }

    private onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const tabs = this.enabledTabs();
        switch (e.key) {
            case 'ArrowRight': e.preventDefault(); this.move(1); break;
            case 'ArrowLeft': e.preventDefault(); this.move(-1); break;
            case 'Home': if (tabs.length) { e.preventDefault(); this.select(tabs[0].id, true); } break;
            case 'End': if (tabs.length) { e.preventDefault(); this.select(tabs[tabs.length - 1].id, true); } break;
            default: break;
        }
    };

    render() {
        const { tabs, selectedId, label, panelIdPrefix, idPrefix, variant, className, testId } = this.props;
        const prefix = idPrefix || 'tab';
        const roving = this.rovingId();
        return (
            <div
                role="tablist"
                aria-label={label}
                className={`${styles.strip} ${variant === 'bar' ? styles.bar : ''} ${className || ''}`}
                data-testid={testId || 'tab-strip'}
                onKeyDown={this.onKeyDown}
            >
                {tabs.map((tab) => {
                    const selected = tab.id === selectedId;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            id={`${prefix}-${tab.id}`}
                            ref={this.setRef(tab.id)}
                            className={`${styles.tab} ${selected ? styles.selected : ''}`}
                            aria-selected={selected}
                            aria-controls={panelIdPrefix ? `${panelIdPrefix}-${tab.id}` : undefined}
                            // The roving half: exactly one tab is reachable with Tab.
                            tabIndex={tab.id === roving ? 0 : -1}
                            disabled={tab.disabled}
                            data-testid={`${prefix}-${tab.id}`}
                            onClick={() => this.select(tab.id, false)}
                        >
                            {tab.icon}
                            {tab.label}
                            {tab.badge && <span className={styles.badge} aria-hidden="true">{tab.badge}</span>}
                        </button>
                    );
                })}
            </div>
        );
    }
}

export default TabStripComponent;
